/**
 * ADR-0155 — pure display logic for Microsoft 365 mirroring surfaces.
 * Extracted here (not in components) so the console test glob covers it.
 */

export type MirrorState = "mirrored" | "pending" | "diverged" | "failed" | "retired";

export interface MirrorArtifactStatus {
  state: MirrorState;
  target: string;
  last_verified_at: string | null;
}

/** Study-detail pill per §7: pillOk / pillNeutral / pillWarn / pillBad. */
export function mirrorPill(state: MirrorState): { className: string; label: string } {
  switch (state) {
    case "mirrored":
      return { className: "pill pillOk", label: "Mirrored" };
    case "pending":
      return { className: "pill pillNeutral", label: "Pending" };
    case "diverged":
      return { className: "pill pillWarn", label: "Diverged" };
    case "failed":
      return { className: "pill pillBad", label: "Failed" };
    case "retired":
      return { className: "pill pillNeutral", label: "Not synced" };
  }
}

/** The re-mirror gesture applies to rows a human can meaningfully resolve. */
export function canRemirror(state: MirrorState): boolean {
  return state === "diverged" || state === "failed";
}

export interface MicrosoftConnectionInfo {
  configured: boolean;
  consent_status: "pending" | "granted" | "revoked";
  dependent_policy_count: number;
}

/**
 * The connect card's state line (§8.3). "Consented — awaiting site access" is
 * a NAMED, NORMAL state (consent and the site grant are independent gestures,
 * possibly weeks apart) — derived as: consent granted but no policy has ever
 * validated against a site yet.
 */
export function consentDisplay(info: MicrosoftConnectionInfo): {
  pillClass: string;
  label: string;
  hint: string | null;
} {
  if (!info.configured) {
    return {
      pillClass: "pill pillNeutral",
      label: "Not configured",
      hint: "The Microsoft Graph application credentials are not set up on this Atlas installation."
    };
  }
  if (info.consent_status === "granted") {
    if (info.dependent_policy_count > 0) {
      return { pillClass: "pill pillOk", label: "Connected", hint: null };
    }
    return {
      pillClass: "pill pillWarn",
      label: "Consented — awaiting site access",
      hint: "Your Microsoft admin approved Atlas. Next: they grant access to the SharePoint site(s) Atlas should write into (decision document, section 6), then verify below."
    };
  }
  if (info.consent_status === "revoked") {
    return {
      pillClass: "pill pillBad",
      label: "Consent revoked",
      hint: "Your Microsoft admin revoked Atlas's access. Send a fresh consent package to reconnect."
    };
  }
  return {
    pillClass: "pill pillNeutral",
    label: "Awaiting admin consent",
    hint: "Send the consent package to your Microsoft 365 administrator — the pack explains everything they need to decide."
  };
}

/** Policy-card rollup line: "3 mirrored · 1 pending · 2 need attention". */
export function rollupLine(rollup: Partial<Record<MirrorState, number>>): string {
  const mirrored = rollup.mirrored ?? 0;
  const pending = rollup.pending ?? 0;
  const attention = (rollup.diverged ?? 0) + (rollup.failed ?? 0);
  const parts: string[] = [];
  if (mirrored > 0) parts.push(`${mirrored} mirrored`);
  if (pending > 0) parts.push(`${pending} pending`);
  if (attention > 0) parts.push(`${attention} need${attention === 1 ? "s" : ""} attention`);
  return parts.length > 0 ? parts.join(" · ") : "Nothing mirrored yet";
}
